/**
 * Intake — the front door, not a second list (Brett, 2026-09-29).
 *
 * The board is the single funnel: every request is born in New /
 * unscoped on Top Projects and is approved (→ Next up) or declined
 * (→ off the board) THERE, with the kickstart-brief state on the row.
 * This page keeps what only it can do — the Freshservice project-ticket
 * inbox (promote a ticket → it appears in New / unscoped) — plus three
 * tiles that say where the funnel stands and link to the board.
 */
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  CalendarClock,
  Inbox,
} from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { ServiceDeskInbox } from "@/components/intake/ServiceDeskInbox";
import { usePortfolio } from "@/hooks/usePortfolio";
import { saveSeenRequestIds } from "@/lib/intakeSeen";
import { cn } from "@/lib/cn";
import type { Project } from "@/types/project";

export function Intake() {
  const portfolio = usePortfolio();
  const navigate = useNavigate();

  // ---- Derived counts ---------------------------------------------------
  const allProjects = useMemo<Project[]>(
    () => (Array.isArray(portfolio.data) ? portfolio.data : []),
    [portfolio.data],
  );
  const active = useMemo(
    () => allProjects.filter((p) => !p.archived_at),
    [allProjects],
  );
  // Every open request lives in New / unscoped on the board (2026-09-29).
  const requested = useMemo(
    () => active.filter((p) => p.status === "requested"),
    [active],
  );
  // Informational: how many of the queue still have no kickstart brief.
  const noBrief = useMemo(
    () =>
      requested.filter(
        (p) => !["submitted", "approved", "not_required"].includes((p.brief_status ?? "").toLowerCase()),
      ).length,
    [requested],
  );
  const benched = useMemo(
    () =>
      active.filter(
        (p) => p.status === "approved" || p.status === "ready_for_development",
      ),
    [active],
  );

  // Landing on /intake clears the sidebar's "new request" badge.
  useEffect(() => {
    if (!Array.isArray(portfolio.data)) return;
    const userRequestIds = portfolio.data
      .filter((p) => p.source === "user_request" && p.status === "requested")
      .map((p) => p.id);
    saveSeenRequestIds(userRequestIds);
  }, [portfolio.data]);

  return (
    <>
      <TopBar
        title="Intake"
        subtitle="The front door — Service Desk tickets become requests here; decisions happen on the Top Projects board."
      />

      <div className="space-y-6 p-8">
        {/* Context row: where requests come from (helpdesk) and where
            they go next (Next up on Top Projects). Both navigate away —
            this page holds only the decision itself. */}
        <div className="grid gap-4 sm:grid-cols-3">
          <KpiTile
            label="Awaiting a decision"
            value={requested.length}
            tone="amber"
            sublabel={requested.length === 0 ? "Caught up" : "In New / unscoped on Top Projects — approve or decline there"}
            icon={<AlertCircle className="h-4 w-4" />}
            headline
            onClick={() => navigate("/top-projects")}
          />
          <KpiTile
            label="No kickstart brief yet"
            value={noBrief}
            tone="slate"
            sublabel="Mark the brief received on the request's board row"
            icon={<Inbox className="h-4 w-4" />}
          />
          <KpiTile
            label="Approved, in the backlog"
            value={benched.length}
            tone="emerald"
            sublabel="Ranked in Next up on Top Projects"
            icon={<CalendarClock className="h-4 w-4" />}
            onClick={() => navigate("/top-projects")}
          />
        </div>

        {/* Front door (2026-09-18): Freshservice project tickets, promoted
            here so ticket → Requested → Approved happens on one page. */}
        <ServiceDeskInbox />

        <div className="flex items-center gap-1.5 text-[11px] text-ink-subtle" data-testid="intake-funnel-note">
          <ArrowRight className="h-3 w-3" />
          New requests appear in New / unscoped on{" "}
          <button
            onClick={() => navigate("/top-projects")}
            className="font-medium text-brand-navy-600 dark:text-brand-navy-400 hover:underline"
          >
            Top Projects
          </button>
          {" "}— approve or decline them there; approved work moves to Next up.
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// KPI tile — clickable when not disabled
// ---------------------------------------------------------------------------
function KpiTile({
  label,
  value,
  sublabel,
  tone,
  icon,
  onClick,
  disabled,
  headline,
}: {
  label: string;
  value: number;
  sublabel: string;
  tone: "slate" | "amber" | "emerald";
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  headline?: boolean;
}) {
  const toneClasses = {
    slate: "ring-line hover:ring-line-strong",
    amber: "ring-amber-200 dark:ring-amber-500/30 hover:ring-amber-300 bg-amber-50/30 dark:bg-amber-500/10",
    emerald: "ring-emerald-200 dark:ring-emerald-500/30 hover:ring-emerald-300",
  };
  const valueColor = {
    slate: "text-ink-2",
    amber: "text-amber-700 dark:text-amber-300",
    emerald: "text-emerald-700 dark:text-emerald-300",
  };
  const iconColor = {
    slate: "text-ink-subtle",
    amber: "text-amber-500",
    emerald: "text-emerald-500",
  };

  const Wrapper = (onClick && !disabled ? "button" : "div") as
    | "button"
    | "div";

  return (
    <Wrapper
      onClick={onClick && !disabled ? onClick : undefined}
      className={cn(
        "rounded-xl bg-surface p-5 ring-1 text-left transition-all",
        toneClasses[tone],
        onClick && !disabled && "cursor-pointer hover:shadow-sm",
        disabled && "opacity-60 cursor-not-allowed",
      )}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
            {label}
          </div>
          <div
            className={cn(
              "mt-1 tabular-nums font-bold",
              valueColor[tone],
              headline ? "text-3xl" : "text-2xl",
            )}
          >
            {value}
          </div>
          <div className="mt-1 text-[11px] text-ink-muted">{sublabel}</div>
        </div>
        <div className={cn("mt-0.5", iconColor[tone])}>{icon}</div>
      </div>
    </Wrapper>
  );
}
